import React, { useState } from 'react';
import { Typography, Box, Paper, FormControlLabel, Switch, FormGroup, Grid, Divider, Chip } from '@mui/material';
import { useScenarios } from '../../context/ScenarioContext';
import { useSettings } from '../../context/SettingsContext';
import SectionHeader from '../common/SectionHeader';
import SectionNavigation from '../common/SectionNavigation';
import AdditionalIncomeForm from '../scenarios/AdditionalIncomeForm';

function OtherIncomeSection({ onNextSection, onPreviousSection, currentSectionIndex, sectionNames }) {
  const { scenarios, handleScenarioChange, toggleIncomeForScenario, isIncomeIncludedInScenario } = useScenarios();
  const { expandedSections } = useSettings();
  
  // Get the master list of all income sources from the first scenario
  // In this app's design, all income sources are stored in the first scenario
  const allIncomeSources = scenarios[0]?.additionalIncomes || [];
  
  return (
    <div className="calculator-section">
      <SectionHeader title="Other Income" />
      
      <div className="other-income-section">
        {scenarios.length > 0 ? (
          <>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Additional Income Sources (Optional)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Define income items that can be included in different scenarios, such as spousal income, rent, or annuities.
              </Typography>
              
              {/* Global income form that applies to all scenarios */}
              <Box sx={{ mt: 2 }}>
                <AdditionalIncomeForm 
                  scenario={scenarios[0]} 
                  handleScenarioChange={handleScenarioChange}
                />
              </Box>
            </Paper>
            
            {allIncomeSources.length > 0 && (
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Manage Income Source Allocations
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Review and update which scenarios include each income source below.
                </Typography>
                
                {allIncomeSources.map(income => (
                  <Box key={income.id} sx={{ mt: 2, mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {income.description || `${income.type} Income`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(income.amount)} {income.frequency === 'ongoing' ? 'per year' : '(one-time)'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle2" gutterBottom>Include in scenarios:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {scenarios.map(scenario => (
                            <FormControlLabel
                              key={`${income.id}-${scenario.id}`}
                              control={
                                <Switch
                                  size="small"
                                  checked={isIncomeIncludedInScenario(income.id, scenario.id)}
                                  onChange={(e) => toggleIncomeForScenario(income.id, scenario.id, e.target.checked)}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: scenario.color,
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: scenario.color,
                                    },
                                  }}
                                />
                              }
                              label={
                                <Chip 
                                  label={scenario.name} 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: `${scenario.color}40`,
                                    borderColor: scenario.color,
                                    border: '1px solid',
                                    color: 'text.primary'
                                  }}
                                />
                              }
                            />
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </Paper>
            )}
          </>
        ) : (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              No Scenarios Available
            </Typography>
            <Typography variant="body1">
              Please create at least one retirement scenario first.
            </Typography>
          </Paper>
        )}
      </div>
      
      <SectionNavigation
        onNextSection={onNextSection}
        onPreviousSection={onPreviousSection}
        currentSectionIndex={currentSectionIndex}
        sectionNames={sectionNames}
      />
    </div>
  );
}

export default OtherIncomeSection;
